import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { useToast } from '../hooks/use-toast';
import { 
  Users, UserPlus, Key, Mail, Trash2, Eye, EyeOff, 
  Send, RefreshCw, Loader2, Search, FileText, ArrowLeft,
  Shield, Clock, CheckCircle, XCircle, ExternalLink, 
  MessageSquare, Calendar, Scale, AlertCircle, User
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ConfiguracionPortalCliente = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [clientes, setClientes] = useState([]);
  const [contactos, setContactos] = useState([]);
  const [expedientes, setExpedientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dialogs
  const [showNuevoClienteDialog, setShowNuevoClienteDialog] = useState(false);
  const [showExpedientesDialog, setShowExpedientesDialog] = useState(false);
  const [showVistaClienteDialog, setShowVistaClienteDialog] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [clienteExpedientes, setClienteExpedientes] = useState([]);
  
  // Vista como cliente
  const [vistaClienteData, setVistaClienteData] = useState(null);
  const [vistaExpedientes, setVistaExpedientes] = useState([]);
  const [vistaExpedienteActivo, setVistaExpedienteActivo] = useState(null);
  const [vistaDetalleExp, setVistaDetalleExp] = useState(null);
  const [vistaMensajes, setVistaMensajes] = useState([]);
  const [vistaLoading, setVistaLoading] = useState(false);
  
  // Nuevo cliente form
  const [nuevoCliente, setNuevoCliente] = useState({
    contacto_id: '',
    email: '',
    expedientes_ids: []
  });
  
  // Search
  const [searchTerm, setSearchTerm] = useState('');
  const [searchContacto, setSearchContacto] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [clientesRes, contactosRes, expedientesRes] = await Promise.all([
        axios.get(`${API_URL}/api/portal-cliente/clientes`),
        axios.get(`${API_URL}/api/contactos`),
        axios.get(`${API_URL}/api/expedientes`)
      ]);
      setClientes(clientesRes.data.clientes || []);
      setContactos(contactosRes.data || []);
      setExpedientes(expedientesRes.data || []);
    } catch (error) {
      console.error('Error cargando datos:', error);
      toast({ title: 'Error', description: 'No se pudieron cargar los datos', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleRegistrarCliente = async () => {
    if (!nuevoCliente.contacto_id || !nuevoCliente.email) {
      toast({ title: 'Error', description: 'Seleccione un contacto y proporcione un email', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API_URL}/api/portal-cliente/registrar-con-envio`, {
        contacto_id: parseInt(nuevoCliente.contacto_id),
        email: nuevoCliente.email,
        expedientes_ids: nuevoCliente.expedientes_ids.map(id => parseInt(id))
      });

      if (response.data.success) {
        toast({ 
          title: 'Cliente registrado', 
          description: response.data.email_enviado 
            ? `Código enviado a ${nuevoCliente.email}` 
            : `Registrado. Código: ${response.data.codigo_acceso} (Email no configurado)`
        });
        setShowNuevoClienteDialog(false);
        setNuevoCliente({ contacto_id: '', email: '', expedientes_ids: [] });
        loadData();
      }
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'No se pudo registrar el cliente', 
        variant: 'destructive' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleCliente = async (clienteId) => {
    try {
      await axios.put(`${API_URL}/api/portal-cliente/clientes/${clienteId}/toggle`);
      toast({ title: 'Estado actualizado' });
      loadData();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo actualizar el estado', variant: 'destructive' });
    }
  };

  const handleRegenerarCodigo = async (clienteId) => {
    try {
      const response = await axios.post(`${API_URL}/api/portal-cliente/clientes/${clienteId}/regenerar-codigo`);
      toast({ 
        title: 'Código regenerado', 
        description: `Nuevo código: ${response.data.nuevo_codigo}` 
      });
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo regenerar el código', variant: 'destructive' });
    }
  };

  const handleEnviarCodigo = async (clienteId, email) => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/portal-cliente/clientes/${clienteId}/enviar-codigo`);
      toast({ title: 'Código enviado', description: `Email enviado a ${email}` });
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'No se pudo enviar el email', 
        variant: 'destructive' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEliminarCliente = async (clienteId) => {
    if (!window.confirm('¿Está seguro de eliminar este cliente del portal? Se eliminarán todos sus accesos.')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/portal-cliente/clientes/${clienteId}`);
      toast({ title: 'Cliente eliminado' });
      loadData();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo eliminar el cliente', variant: 'destructive' });
    }
  };

  const handleVerExpedientes = async (cliente) => {
    setSelectedCliente(cliente);
    try {
      const response = await axios.get(`${API_URL}/api/portal-cliente/clientes/${cliente.id}/expedientes`);
      setClienteExpedientes(response.data.expedientes || []);
      setShowExpedientesDialog(true);
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudieron cargar los expedientes', variant: 'destructive' });
    }
  };

  const handleAsignarExpediente = async (expedienteId) => {
    if (!selectedCliente) return;
    
    try {
      await axios.post(`${API_URL}/api/portal-cliente/clientes/${selectedCliente.id}/expedientes/${expedienteId}`);
      toast({ title: 'Expediente asignado' });
      handleVerExpedientes(selectedCliente); // Recargar
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'No se pudo asignar el expediente', 
        variant: 'destructive' 
      });
    }
  };

  const handleQuitarExpediente = async (expedienteId) => {
    if (!selectedCliente) return;
    
    try {
      await axios.delete(`${API_URL}/api/portal-cliente/clientes/${selectedCliente.id}/expedientes/${expedienteId}`);
      toast({ title: 'Acceso eliminado' });
      handleVerExpedientes(selectedCliente); // Recargar
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo eliminar el acceso', variant: 'destructive' });
    }
  };

  // Ver como cliente - cargar datos del portal
  const handleVerComoCliente = async (cliente) => {
    setVistaClienteData(cliente);
    setVistaLoading(true);
    setShowVistaClienteDialog(true);
    setVistaExpedienteActivo(null);
    setVistaDetalleExp(null);
    setVistaMensajes([]);

    try {
      // Cargar expedientes del cliente
      const response = await axios.get(`${API_URL}/api/portal-cliente/mis-expedientes?cliente_email=${cliente.email}`);
      const exps = response.data.expedientes || [];
      setVistaExpedientes(exps);
      
      // Si hay expedientes, cargar el primero
      if (exps.length > 0) {
        handleVistaSelectExpediente(exps[0].id, cliente.email);
      }
    } catch (error) {
      console.error('Error cargando vista cliente:', error);
      toast({ title: 'Error', description: 'No se pudo cargar la vista del cliente', variant: 'destructive' });
    } finally {
      setVistaLoading(false);
    }
  };

  const handleVistaSelectExpediente = async (expId, clienteEmail) => {
    setVistaExpedienteActivo(expId);
    
    try {
      // Cargar detalle del expediente
      const detalleRes = await axios.get(`${API_URL}/api/portal-cliente/expediente/${expId}?cliente_email=${clienteEmail}`);
      setVistaDetalleExp(detalleRes.data);
      
      // Cargar mensajes
      const mensajesRes = await axios.get(`${API_URL}/api/portal-cliente/mensajes/${expId}?cliente_email=${clienteEmail}`);
      setVistaMensajes(mensajesRes.data.mensajes || []);
    } catch (error) {
      console.error('Error cargando detalle:', error);
    }
  };

  // Filtrar clientes
  const filteredClientes = clientes.filter(c => 
    c.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filtrar contactos para nuevo cliente
  const filteredContactos = contactos.filter(c => 
    c.tipo !== 'organismo' &&
    (c.nombre?.toLowerCase().includes(searchContacto.toLowerCase()) ||
     c.email?.toLowerCase().includes(searchContacto.toLowerCase()))
  );

  // Expedientes que el cliente NO tiene asignados
  const expedientesDisponibles = expedientes.filter(
    exp => !clienteExpedientes.find(ce => ce.id === exp.id)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/configuracion')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="w-6 h-6 text-blue-600" />
              Portal del Cliente
            </h1>
            <p className="text-gray-500">Gestione el acceso de clientes a sus expedientes</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => window.open('/portal-cliente', '_blank')}
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Ver Portal
          </Button>
          <Button onClick={() => setShowNuevoClienteDialog(true)} className="bg-blue-600 hover:bg-blue-700">
            <UserPlus className="w-4 h-4 mr-2" />
            Dar acceso a cliente
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-900">¿Cómo funciona el Portal del Cliente?</h3>
              <ul className="text-sm text-blue-700 mt-2 space-y-1">
                <li>• Los clientes acceden con su email y un código único que les enviamos</li>
                <li>• Solo ven los expedientes y actuaciones que usted les asigne</li>
                <li>• Pueden enviar consultas que se gestionan en Atención al Cliente</li>
                <li>• URL del portal: <span className="font-mono bg-blue-100 px-1 rounded">/portal-cliente</span></li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total clientes</p>
                <p className="text-3xl font-bold text-gray-900">{clientes.length}</p>
              </div>
              <Users className="w-10 h-10 text-blue-600 opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Activos</p>
                <p className="text-3xl font-bold text-green-600">{clientes.filter(c => c.activo).length}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-600 opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Inactivos</p>
                <p className="text-3xl font-bold text-gray-400">{clientes.filter(c => !c.activo).length}</p>
              </div>
              <XCircle className="w-10 h-10 text-gray-400 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input 
            placeholder="Buscar cliente..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Clients Table */}
      <Card>
        <CardHeader>
          <CardTitle>Clientes con acceso al portal</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredClientes.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900">No hay clientes registrados</h3>
              <p className="text-gray-500 mt-1">Añada clientes para que puedan acceder al portal</p>
              <Button onClick={() => setShowNuevoClienteDialog(true)} className="mt-4">
                <UserPlus className="w-4 h-4 mr-2" />
                Dar acceso a cliente
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Código de acceso</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Último acceso</TableHead>
                  <TableHead>Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClientes.map((cliente) => (
                  <TableRow key={cliente.id}>
                    <TableCell className="font-medium">{cliente.nombre}</TableCell>
                    <TableCell>{cliente.email}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                          {cliente.codigo_acceso || '••••••••'}
                        </code>
                        {cliente.codigo_acceso && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(cliente.codigo_acceso);
                                toast({ title: 'Copiado', description: 'Código copiado al portapapeles' });
                              } catch (err) {
                                // Fallback para navegadores que no soportan clipboard
                                const textArea = document.createElement('textarea');
                                textArea.value = cliente.codigo_acceso;
                                document.body.appendChild(textArea);
                                textArea.select();
                                document.execCommand('copy');
                                document.body.removeChild(textArea);
                                toast({ title: 'Copiado', description: 'Código copiado al portapapeles' });
                              }
                            }}
                            title="Copiar código"
                            className="h-6 w-6 p-0"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </Button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch 
                          checked={cliente.activo} 
                          onCheckedChange={() => handleToggleCliente(cliente.id)}
                        />
                        <Badge variant={cliente.activo ? "default" : "secondary"}>
                          {cliente.activo ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      {cliente.ultimo_acceso 
                        ? new Date(cliente.ultimo_acceso).toLocaleDateString('es-ES', { 
                            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                          })
                        : <span className="text-gray-400">Nunca</span>
                      }
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleVerComoCliente(cliente)}
                          title="Ver como cliente"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleVerExpedientes(cliente)}
                          title="Ver expedientes"
                        >
                          <FileText className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleEnviarCodigo(cliente.id, cliente.email)}
                          disabled={saving}
                          title="Enviar código por email"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleRegenerarCodigo(cliente.id)}
                          title="Regenerar código"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleEliminarCliente(cliente.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog: Nuevo Cliente */}
      <Dialog open={showNuevoClienteDialog} onOpenChange={setShowNuevoClienteDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Dar acceso al Portal del Cliente
            </DialogTitle>
            <DialogDescription>
              Seleccione un contacto existente para darle acceso al portal
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Buscar contacto */}
            <div>
              <Label>Buscar contacto</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input 
                  placeholder="Buscar por nombre o email..."
                  value={searchContacto}
                  onChange={(e) => setSearchContacto(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Lista de contactos */}
            <div className="border rounded-lg max-h-[200px] overflow-y-auto">
              {filteredContactos.slice(0, 20).map(contacto => (
                <div 
                  key={contacto.id}
                  onClick={() => {
                    setNuevoCliente({
                      ...nuevoCliente,
                      contacto_id: contacto.id,
                      email: contacto.email || ''
                    });
                  }}
                  className={`p-3 cursor-pointer hover:bg-blue-50 border-b last:border-b-0 ${
                    nuevoCliente.contacto_id === contacto.id ? 'bg-blue-100' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{contacto.nombre}</p>
                      <p className="text-sm text-gray-500">{contacto.email || 'Sin email'}</p>
                    </div>
                    {nuevoCliente.contacto_id === contacto.id && (
                      <CheckCircle className="w-5 h-5 text-blue-600" />
                    )}
                  </div>
                </div>
              ))}
              {filteredContactos.length === 0 && (
                <div className="p-4 text-center text-gray-500">
                  No se encontraron contactos
                </div>
              )}
            </div>

            {/* Email */}
            {nuevoCliente.contacto_id && (
              <div>
                <Label>Email de acceso *</Label>
                <Input 
                  type="email"
                  value={nuevoCliente.email}
                  onChange={(e) => setNuevoCliente({...nuevoCliente, email: e.target.value})}
                  placeholder="email@ejemplo.com"
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">Se enviará el código de acceso a este email</p>
              </div>
            )}

            {/* Expedientes */}
            {nuevoCliente.contacto_id && (
              <div>
                <Label>Expedientes a los que tendrá acceso</Label>
                <div className="border rounded-lg max-h-[150px] overflow-y-auto mt-1">
                  {expedientes.map(exp => (
                    <label 
                      key={exp.id}
                      className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                    >
                      <input 
                        type="checkbox"
                        checked={nuevoCliente.expedientes_ids.includes(exp.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNuevoCliente({
                              ...nuevoCliente,
                              expedientes_ids: [...nuevoCliente.expedientes_ids, exp.id]
                            });
                          } else {
                            setNuevoCliente({
                              ...nuevoCliente,
                              expedientes_ids: nuevoCliente.expedientes_ids.filter(id => id !== exp.id)
                            });
                          }
                        }}
                        className="w-4 h-4 text-blue-600"
                      />
                      <div>
                        <p className="font-medium text-sm">{exp.numero_expediente}</p>
                        <p className="text-xs text-gray-500">{exp.titulo}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNuevoClienteDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleRegistrarCliente}
              disabled={saving || !nuevoCliente.contacto_id || !nuevoCliente.email}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Key className="w-4 h-4 mr-2" />}
              Crear acceso y enviar código
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Gestionar Expedientes */}
      <Dialog open={showExpedientesDialog} onOpenChange={setShowExpedientesDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Expedientes de {selectedCliente?.nombre}</DialogTitle>
            <DialogDescription>
              Gestione los expedientes a los que tiene acceso este cliente
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Expedientes asignados */}
            <div>
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Expedientes con acceso ({clienteExpedientes.length})
              </h3>
              {clienteExpedientes.length === 0 ? (
                <p className="text-gray-500 text-sm p-4 bg-gray-50 rounded-lg">
                  No tiene expedientes asignados
                </p>
              ) : (
                <div className="border rounded-lg divide-y">
                  {clienteExpedientes.map(exp => (
                    <div key={exp.id} className="p-3 flex items-center justify-between">
                      <div>
                        <p className="font-medium">{exp.numero_expediente}</p>
                        <p className="text-sm text-gray-500">{exp.titulo}</p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleQuitarExpediente(exp.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Añadir más expedientes */}
            <div>
              <h3 className="font-semibold mb-2">Añadir expediente</h3>
              <Select onValueChange={(value) => handleAsignarExpediente(parseInt(value))}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar expediente..." />
                </SelectTrigger>
                <SelectContent>
                  {expedientesDisponibles.map(exp => (
                    <SelectItem key={exp.id} value={String(exp.id)}>
                      {exp.numero_expediente} - {exp.titulo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExpedientesDialog(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Vista como Cliente */}
      <Dialog open={showVistaClienteDialog} onOpenChange={setShowVistaClienteDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden p-0">
          {/* Header del Portal */}
          <div className="bg-gradient-to-r from-blue-800 to-blue-600 text-white p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Scale className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="font-bold">Portal del Cliente - Vista Previa</h2>
                  <p className="text-sm text-blue-100">
                    {vistaClienteData?.nombre} ({vistaClienteData?.email})
                  </p>
                </div>
              </div>
              <Badge className="bg-yellow-500 text-yellow-900">
                <Eye className="w-3 h-3 mr-1" />
                Modo Administrador
              </Badge>
            </div>
          </div>

          {/* Contenido */}
          <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 120px)' }}>
            {vistaLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : vistaExpedientes.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">Sin expedientes asignados</h3>
                <p className="text-gray-500">Este cliente no tiene expedientes asignados al portal</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                {/* Lista de expedientes */}
                <div className="lg:col-span-1">
                  <h3 className="font-semibold text-gray-700 mb-2 text-sm">Mis Expedientes</h3>
                  <div className="space-y-2">
                    {vistaExpedientes.map(exp => (
                      <button
                        key={exp.id}
                        onClick={() => handleVistaSelectExpediente(exp.id, vistaClienteData?.email)}
                        className={`w-full text-left p-3 rounded-lg border transition-colors ${
                          vistaExpedienteActivo === exp.id 
                            ? 'bg-blue-50 border-blue-300' 
                            : 'bg-white hover:bg-gray-50 border-gray-200'
                        }`}
                      >
                        <p className="font-medium text-sm">{exp.numero_expediente}</p>
                        <p className="text-xs text-gray-500 truncate">{exp.titulo}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Detalle del expediente */}
                <div className="lg:col-span-3">
                  {vistaDetalleExp ? (
                    <Tabs defaultValue="estado" className="w-full">
                      <TabsList className="grid w-full grid-cols-3">
                        <TabsTrigger value="estado">Estado</TabsTrigger>
                        <TabsTrigger value="actuaciones">Actuaciones</TabsTrigger>
                        <TabsTrigger value="mensajes">Mensajes</TabsTrigger>
                      </TabsList>

                      {/* Tab Estado */}
                      <TabsContent value="estado" className="mt-4">
                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-lg">{vistaDetalleExp.numero_expediente}</CardTitle>
                            <CardDescription>{vistaDetalleExp.titulo}</CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div className="space-y-3">
                                <div>
                                  <span className="text-gray-500">Estado:</span>
                                  <Badge className="ml-2" variant={vistaDetalleExp.estado === 'activo' ? 'default' : 'secondary'}>
                                    {vistaDetalleExp.estado || 'Activo'}
                                  </Badge>
                                </div>
                                <div>
                                  <span className="text-gray-500">Fase:</span>
                                  <span className="ml-2 font-medium">{vistaDetalleExp.fase || 'No especificada'}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Fecha inicio:</span>
                                  <span className="ml-2">{vistaDetalleExp.fecha_inicio ? new Date(vistaDetalleExp.fecha_inicio).toLocaleDateString('es-ES') : 'N/A'}</span>
                                </div>
                              </div>
                              <div className="space-y-3">
                                <div>
                                  <span className="text-gray-500">Juzgado:</span>
                                  <span className="ml-2">{vistaDetalleExp.juzgado || 'No asignado'}</span>
                                </div>
                                {vistaDetalleExp.descripcion && (
                                  <div>
                                    <span className="text-gray-500">Descripción:</span>
                                    <p className="mt-1 text-gray-700">{vistaDetalleExp.descripcion}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </TabsContent>

                      {/* Tab Actuaciones */}
                      <TabsContent value="actuaciones" className="mt-4">
                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-lg flex items-center gap-2">
                              <Calendar className="w-5 h-5" />
                              Actuaciones visibles para el cliente
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            {vistaDetalleExp.actuaciones?.length > 0 ? (
                              <div className="space-y-3">
                                {vistaDetalleExp.actuaciones.map((act, idx) => (
                                  <div key={idx} className="p-3 bg-gray-50 rounded-lg border-l-4 border-blue-500">
                                    <div className="flex items-start justify-between">
                                      <div>
                                        <p className="font-medium">{act.tipo}</p>
                                        <p className="text-sm text-gray-600">{act.descripcion}</p>
                                      </div>
                                      <span className="text-xs text-gray-500">
                                        {act.fecha ? new Date(act.fecha).toLocaleDateString('es-ES') : ''}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center py-8 text-gray-500">
                                <Calendar className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                                <p>No hay actuaciones visibles</p>
                                <p className="text-xs">Las actuaciones se muestran cuando están marcadas como visibles para el cliente</p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </TabsContent>

                      {/* Tab Mensajes */}
                      <TabsContent value="mensajes" className="mt-4">
                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-lg flex items-center gap-2">
                              <MessageSquare className="w-5 h-5" />
                              Consultas y Respuestas
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            {vistaMensajes.length > 0 ? (
                              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                                {vistaMensajes.map((msg, idx) => (
                                  <div 
                                    key={idx} 
                                    className={`p-3 rounded-lg ${
                                      msg.tipo === 'cliente' 
                                        ? 'bg-blue-50 border border-blue-200 ml-8' 
                                        : 'bg-gray-50 border border-gray-200 mr-8'
                                    }`}
                                  >
                                    <div className="flex items-center gap-2 mb-1">
                                      <User className="w-3 h-3" />
                                      <span className="text-xs font-medium">
                                        {msg.tipo === 'cliente' ? 'Cliente' : 'Abogado'}
                                      </span>
                                      <span className="text-xs text-gray-500">
                                        {msg.fecha ? new Date(msg.fecha).toLocaleString('es-ES') : ''}
                                      </span>
                                    </div>
                                    <p className="text-sm">{msg.contenido}</p>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center py-8 text-gray-500">
                                <MessageSquare className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                                <p>No hay mensajes</p>
                                <p className="text-xs">Aquí aparecerán las consultas del cliente y las respuestas</p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </TabsContent>
                    </Tabs>
                  ) : (
                    <div className="flex items-center justify-center py-12 text-gray-500">
                      <p>Seleccione un expediente para ver los detalles</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t p-3 bg-gray-50 flex justify-end">
            <Button variant="outline" onClick={() => setShowVistaClienteDialog(false)}>
              Cerrar vista previa
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConfiguracionPortalCliente;
